const SAVAGE_API_URL = 'https://func.rc.savagebet.gg';

// TODO: Replace with environment variables
const CLIENT_ID =
    process.env.VUE_APP_SAVAGE_CLIENT_ID ||
    '2c1e3a67-1635-488c-ba4d-dfd87b70ec26';
const CLIENT_SECRET =
    process.env.VUE_APP_SAVAGE_CLIENT_SECRET ||
    '004cc2b0-a302-43fe-bf56-412838700286';

interface ISavageAuthResponse {
    vendorId: string;
    jwt: string;
    pubsub: {
        token: string;
        baseUrl: string;
        url: string;
    };
}

/**
 * Generic helper function for calling Savage API endpoints.
 *
 * @param path - The API path (e.g., "/api/v1/activity/betPlaced").
 * @param method - HTTP method (GET, POST, PATCH, etc.).
 * @param data - Optional JSON payload to send in the request body.
 * @returns Resolves to the JSON response from the API.
 * @throws Throws if the API returns a non-OK response.
 */
async function callSavageApi<T = any>(
    path: string,
    method: string = 'GET',
    data: any = null,
): Promise<T> {
    const url = `${SAVAGE_API_URL}${path}`;

    const options: RequestInit = {
        method,
        headers: {
            'x-client-id': CLIENT_ID,
            'x-client-secret': CLIENT_SECRET,
            'Content-Type': 'application/json',
        },
    };

    if (data) {
        options.body = JSON.stringify(data);
    }

    const response = await fetch(url, options);
    if (!response.ok) {
        throw new Error(
            `Savage API Error: ${response.status} ${response.statusText}`,
        );
    }
    return response.json();
}

// ---------------------------------------------------------------------------
//  AUTHENTICATION
// ---------------------------------------------------------------------------

/**
 * Authorizes a player with Savage API and returns a unique JWT for the player.
 *
 * @param playerId - Unique player identifier generated by your system.
 * @param currency - 3-letter currency code (e.g., "USD").
 * @returns Returns an object containing JWT and pubsub credentials.
 */
export async function authorizeSavageVendor(
    playerId: string,
    currency: string,
): Promise<ISavageAuthResponse> {
    return callSavageApi<ISavageAuthResponse>(
        '/api/v1/auth/websocket-tokens',
        'POST',
        {
            playerId,
            currency,
        },
    );
}

// ---------------------------------------------------------------------------
//  PLAYER ACTIVITY
// ---------------------------------------------------------------------------

/**
 * Records a bet placement for a player.
 *
 * @param playerId - Player ID.
 * @param amount - Amount of the bet.
 * @param currency - Currency code (e.g., "USD").
 * @param odds - Decimal odds (e.g., 1.5).
 */
export async function betPlaced(
    playerId: string,
    amount: number,
    currency: string,
    odds: number,
): Promise<void> {
    return callSavageApi('/api/v1/activity/betPlaced', 'POST', {
        playerId,
        amount,
        currency,
        odds,
    });
}

/**
 * Records a deposit made by the player.
 *
 * @param playerId - Player ID.
 * @param amount - Deposit amount.
 * @param currency - Currency code (e.g., "USD").
 */
export async function depositMade(
    playerId: string,
    amount: number,
    currency: string,
): Promise<void> {
    return callSavageApi('/api/v1/activity/depositMade', 'POST', {
        playerId,
        amount,
        currency,
    });
}

/**
 * Resets the player's game state, including rewards and purchases.
 *
 * @param playerId - Player ID.
 * @param fullReset - If true, performs a complete reset.
 */
export async function resetGame(
    playerId: string,
    fullReset: boolean = true,
): Promise<void> {
    return callSavageApi(`/api/v1/players/${playerId}/reset`, 'POST', {
        playerId,
        fullReset,
    });
}

// ---------------------------------------------------------------------------
//  VENDOR CONFIGURATION
// ---------------------------------------------------------------------------

/**
 * Retrieves all currencies configured for the vendor account.
 *
 * @returns Array of 3-letter currency codes.
 */
export async function getCurrencies(): Promise<string[]> {
    const data = await callSavageApi<{ currencies: string[] }>(
        '/api/v1/vendor/currencies',
        'GET',
    );
    return data.currencies;
}

/**
 * Sets or updates the assigned currency for a specific player.
 *
 * @param playerId - Player ID.
 * @param currency - 3-letter currency code (e.g., "USD").
 */
export async function setPlayerCurrency(
    playerId: string,
    currency: string,
): Promise<void> {
    return callSavageApi(`/api/v1/players/${playerId}/currency`, 'PATCH', {
        playerId,
        currency,
    });
}
